/* ================================================================ */
// DATA ACTIONS - Mapping | Mapping.js
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import 'dotenv/config';
import PropTypes from 'prop-types';
// Constants
import {LoadingTypes, VisEngineTypes} from '@myglobe/core/dist/types';
import {getMissingIds, getLayersById, getStatesById} from '../../selectors/Data';
// Auth
//import { Auth0Context } from '../../react-auth0-wrapper';
// Data Action Helpers
import {indexes, callAPI, addData, mappify} from '.';
import {fetchDatasets} from './Datasets';
import {updateDeckLayerClasses} from '../Map';
/* ========================================== */
// Actions
/* ========================================== */
export const FETCH_LAYERS = 'FETCH_LAYERS';
export const FETCH_LAYERS_FAILURE = 'FETCH_LAYERS_FAILURE';
export const FETCH_LAYERS_SUCCESS = 'FETCH_LAYERS_SUCCESS';
export const FETCH_STATES = 'FETCH_STATES';
export const FETCH_STATES_FAILURE = 'FETCH_STATES_FAILURE';
export const FETCH_STATES_SUCCESS = 'FETCH_STATES_SUCCESS';

/*
String.prototype.parseFunction = function () {
    var funcReg = /function *\(([^()]*)\)[ \n\t]*{(.*)}/gmi;
    var match = funcReg.exec(this.replace(/\n/g, ' '));
    if(match) {
        return new Function(match[1].split(','), match[2]);
    }
    return null;
};
*/
/* ========================================== */
// Action Functions
/* ========================================== */
/* ========================================== */
/**
 * Fetch Layers
 * ------------
 * Fetches given layer Ids according to load mode
 * either from comparisons against the current state
 * or a simple bulk update
 *
 * @param {array} layerIDs
 */
export function fetchLayers(layerIDs) {
    return(dispatch, getState) => {
        dispatch({type: FETCH_LAYERS});

        var fetchLayerIDs = layerIDs;
        // Check loading mode
        if(process.env.DATA_LOAD_MODE === LoadingTypes.SEQUENTIAL) {
            // Check for already loaded layers
            const currentLayerIds = getLayersById(getState());
            // Check all layer ids are present in loaded layers
            const loadedLayerIDs = layerIDs.filter(id=>currentLayerIds.includes(id));
            // Find missing layers and set specific fetch request ids
            fetchLayerIDs = layerIDs.filter(id=>!loadedLayerIDs.includes(id));
        }

        // External Endpoint Fetch
        if(fetchLayerIDs.length > 0) {
            let query = '{';
            fetchLayerIDs.forEach((id, i)=>query += `layer${i}: visLayerById(id: "${id}") {
                uuid: id
                name
                type
                config
                globeId
                dataset: datasetByDatasetId {
                    id
                    name
                }
                lastUpdated
            }\n`);
            query+='}';

            return callAPI(query)
                .then(response=>{
                    const {data} = response;
                    let datasetMap = {};
                    const formattedData = Object.values(data).map(layer=>{
                        let dataset = layer.dataset;
                        if(dataset) {
                            datasetMap[dataset.name] = dataset.id;
                            dataset = dataset.name;
                        }
                        const config = layer.config || null;
                        // Fix functions
                        /*Object.keys(config).forEach(key=>{
                            const value = config[key];
                            if (/(\w+\s?=>[^\n]+)|(function\([\w\s\,]+\)\s?\{[^\n]+\})/.test(value))
                                config[key] = eval(`(${value})`);
                        });*/
                        const formattedLayer = {
                            ...layer,
                            dataset,
                            config
                        };
                        return formattedLayer;
                    });
                    const payload = mappify(indexes.layer, formattedData);

                    // Get all dataset IDs
                    const datasetIds = [...new Set(Object.values(payload).map(state=>state.dataset))];
                    // Find missing datasets
                    const getUnloadedIds = getMissingIds('datasets', datasetIds);
                    const missingDatasets = getUnloadedIds(getState());
                    const missingDatasetUUIDs = missingDatasets.map(id=>datasetMap[id]);
                    console.debug('Data > Mapping (Actions) | ', 'fetchLayers -', 'missingDatasets:', missingDatasets, 'missingDatasetUUIDs:', missingDatasetUUIDs);
                    // Fetch missing datasets if required
                    if (missingDatasetUUIDs.length > 0)
                        dispatch(fetchDatasets(missingDatasetUUIDs));

                    // Add new layers to data state
                    dispatch(addData('layers', payload));

                    // Get deck layers and process required layer class types
                    const deckLayerClasses = formattedData.filter(layer=>layer.type === VisEngineTypes.DECKGL).map(layer=>layer.config?.type);
                    console.debug('Data > Mapping (Actions) | ', 'fetchLayers -', 'formattedData:', formattedData, 'deckLayerClasses:', deckLayerClasses);
                    if(deckLayerClasses.length > 0)
                        dispatch(updateDeckLayerClasses(deckLayerClasses));
                    // TODO: ASYNC SUCCESS HANDLING
                    dispatch({type: FETCH_LAYERS_SUCCESS});
                })
                .catch(error=>{
                    console.warn(error);
                    dispatch({type: FETCH_LAYERS_FAILURE, error });
                });
        }
    };
}
fetchLayers.propTypes = {
    layerIDs: PropTypes.arrayOf(PropTypes.string).isRequired
};
/* ========================================== */
/**
 * Fetch States
 * ------------
 * Checks the Data state for preloaded states
 * matching the provided stateIDs argument
 * and loads any missing states from the DB
 *
 * @param {array} stateIDs
 */
export function fetchStates(stateIDs) {
    return(dispatch, getState) => {
        dispatch({type: FETCH_STATES, states: stateIDs});

        var fetchStateIds = stateIDs;
        // Check loading mode
        if(process.env.DATA_LOAD_MODE === LoadingTypes.SEQUENTIAL) {
            // Get current state ids
            const currentStateIds = getStatesById(getState());
            // Check all state ids are present in loaded states
            const loadedStateIDs = stateIDs.filter(id=>currentStateIds.includes(id));
            // Remaining States Check
            fetchStateIds = stateIDs.filter(id=>!loadedStateIDs.includes(id));
        }

        // External Endpoint Fetch
        if(fetchStateIds.length > 0) {
            let query = '{';
            fetchStateIds.forEach((id, i)=>query += `state${i}: visState(id: "${id}") {
                uuid: id
                name
                mapStyle
                viewState
                layers: stateLayersByStateId {
                    nodes {
                        layer {
                            id
                            name
                        }
                    }
                }
            }\n`);
            query+='}';

            return callAPI(query)
                .then(response=>{
                    const {data} = response;
                    let layerMap = {};
                    const payload = Object.values(data).reduce((map, obj) => {
                        // Format object layer names into simple array structure
                        console.debug('Data > Mapping (Actions) | ', 'fetchStates -', 'obj.layers:', obj.layers);
                        const formattedObj = {
                            ...obj,
                            //viewState: JSON.parse(obj.viewState),
                            layers: obj.layers.nodes.map(layerNode=>{
                                const index = layerNode.layer[indexes.layer];
                                // Store UUID of layer to layer index in map
                                layerMap[index] = layerNode.layer.id;
                                return index;
                            })
                        };
                        map[obj.name] = formattedObj;
                        return map;
                    }, {});

                    // Get all layers used in states as flat set
                    const layerIDs = [...new Set(Object.values(payload).map(state=>state.layers).flat())];
                    // Get missing layer indexes from current state
                    const getUnloadedIds = getMissingIds('layers', layerIDs);
                    const missingLayers = getUnloadedIds(getState());
                    // Get missing layer uuids for fetch and fetch layers from api
                    const missingLayerUUIDs = missingLayers.map(id=>layerMap[id]);
                    console.debug('MISSING LAYERS', missingLayers, missingLayerUUIDs);
                    if (missingLayerUUIDs.length > 0)
                        dispatch(fetchLayers(missingLayerUUIDs));

                    dispatch(addData('states', payload));
                    // TODO: ASYNC SUCCESS HANDLING
                    dispatch({type: FETCH_STATES_SUCCESS});
                })
                .catch(error=>{
                    console.warn(error);
                    dispatch({type: FETCH_STATES_FAILURE, error });
                });
        }
    };
}
fetchStates.propTypes = {
    stateIDs: PropTypes.arrayOf(PropTypes.string).isRequired
};